import React from "react";
import { cn } from "../utils/cn";

interface ImpactCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: "primary" | "secondary" | "accent";
  trend?: {
    value: number;
    label: string;
  };
}

export default function ImpactCard({
  title,
  value,
  subtitle,
  icon,
  color,
  trend,
}: ImpactCardProps) {
  const colorClasses = {
    primary:
      "bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-600 border-emerald-200",
    secondary:
      "bg-gradient-to-br from-amber-50 to-amber-100 text-amber-600 border-amber-200",
    accent:
      "bg-gradient-to-br from-cyan-50 to-cyan-100 text-cyan-600 border-cyan-200",
  };

  const iconBgClasses = {
    primary: "bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500",
    secondary: "bg-gradient-to-br from-amber-500 via-orange-500 to-red-500",
    accent: "bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-500",
  };

  return (
    <div className="card-floating group">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div
            className={cn(
              "inline-flex p-4 rounded-2xl border-2 mb-6 shadow-lg group-hover:shadow-xl transition-all duration-500 transform group-hover:scale-105 group-hover:rotate-1",
              colorClasses[color]
            )}
          >
            <div
              className={cn(
                "flex items-center justify-center w-8 h-8 rounded-lg text-white shadow-md",
                iconBgClasses[color]
              )}
            >
              {icon}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-sm font-bold text-slate-600 uppercase tracking-wider">
              {title}
            </p>
            <p className="text-3xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-500">
              {value.toLocaleString()}
            </p>
            <p className="text-sm text-slate-500 font-semibold">{subtitle}</p>
          </div>
        </div>
        {trend && (
          <div className="text-right">
            <div
              className={cn(
                "inline-flex items-center px-3 py-2 rounded-lg text-xs font-bold shadow-lg transform group-hover:scale-105 transition-all duration-500",
                trend.value > 0
                  ? "bg-emerald-100 text-emerald-800 border-2 border-emerald-200"
                  : "bg-red-100 text-red-800 border-2 border-red-200"
              )}
            >
              <span className="mr-1 text-sm">
                {trend.value > 0 ? "📈" : "📉"}
              </span>
              {trend.value > 0 ? "+" : ""}
              {trend.value}%
            </div>
            <p className="text-xs text-slate-500 mt-2 font-semibold">
              {trend.label}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
